import { Link } from "react-router-dom";
import { AirplaneTakeoff } from "@phosphor-icons/react";
import { motion } from "motion/react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

const pilotBadgeVariants = cva(
  "relative inline-flex items-center bg-yellow-400 text-yellow-900 font-bold transition-shadow duration-200 overflow-hidden",
  {
    variants: {
      variant: {
        default:
          "pilot-shimmer gap-2.5 px-4 py-2 text-base rounded-xl shadow-[6px_6px_0px_0px_rgba(0,0,0,0.4)] hover:shadow-[6px_8px_0px_0px_rgba(0,0,0,0.4)] dark:shadow-[6px_6px_0px_0px_rgba(255,255,255,0.4)] dark:hover:shadow-[6px_8px_0px_0px_rgba(255,255,255,0.4)]",
        small:
          "pilot-shimmer gap-1.5 px-2 py-1 text-sm rounded-lg shadow-[3px_5px_0px_0px_rgba(0,0,0,0.4)] hover:shadow-[3px_4px_0px_0px_rgba(0,0,0,0.4)] dark:shadow-[3px_5px_0px_0px_rgba(255,255,255,0.4)] dark:hover:shadow-[3px_4px_0px_0px_rgba(255,255,255,0.4)]",
        icon: "gap-0 p-0.5 text-xs rounded-md shadow-[1px_1px_0px_0px_rgba(0,0,0,0.4)] hover:shadow-[1px_2px_0px_0px_rgba(0,0,0,0.4)] dark:shadow-[1px_1px_0px_0px_rgba(255,255,255,0.4)] dark:hover:shadow-[1px_2px_0px_0px_rgba(255,255,255,0.4)]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const getMotionConfig = (
  variant: "default" | "small" | "icon" | null | undefined
) => {
  switch (variant) {
    case "icon":
      return {
        whileHover: { scale: 1.1, y: -1, rotate: 0 },
        staticRotation: "",
      };
    case "small":
      return {
        whileHover: { scale: 1.05, y: -1, rotate: 0 },
        staticRotation: "",
      };
    default:
      return {
        whileHover: { scale: 1.1, y: -3, rotate: -1 },
        staticRotation: "rotate-2 sm:rotate-6",
      };
  }
};

const getIconSize = (
  variant: "default" | "small" | "icon" | null | undefined
) => {
  switch (variant) {
    case "icon":
      return "w-3 h-3";
    case "small":
      return "w-4 h-4";
    default:
      return "w-5 h-5";
  }
};

const getText = (variant: "default" | "small" | "icon" | null | undefined) => {
  switch (variant) {
    case "icon":
      return null;
    case "small":
      return "pilot";
    default:
      return "pilotProgram";
  }
};

const getHref = (
  variant: "default" | "small" | "icon" | null | undefined,
  href: string
) => {
  switch (variant) {
    case "default":
      return href;
    default:
      return "";
  }
};

interface PilotBadgeProps extends VariantProps<typeof pilotBadgeVariants> {
  className?: string;
  href?: string;
}

function PilotBadge({
  className = "",
  variant = "default",
  href = "/settings#pilot-program",
}: PilotBadgeProps) {
  const { t } = useTranslation("common");
  const motionConfig = getMotionConfig(variant);
  const iconSize = getIconSize(variant);
  const text = getText(variant);
  const linkHref = getHref(variant, href);

  // Generate random animation values for each badge instance (only for shimmer variants)
  const randomValues = useMemo(() => {
    if (variant === "icon") return null; // No shimmer for icon variant

    return {
      delay: Math.random() * 7, // Random delay between 0-7 seconds
      startPos: Math.random() * 200 - 100, // Random start position between -200% and 200%
    };
  }, [variant]);

  // CSS custom properties for randomization (only for shimmer variants)
  const shimmerStyle = randomValues
    ? ({
        "--pilot-delay": randomValues.delay,
        "--pilot-start-pos": `${randomValues.startPos}%`,
        "--pilot-end-pos": `${randomValues.startPos - 400}%`, // End position matches the animation cycle
      } as React.CSSProperties)
    : undefined;

  return (
    <motion.div
      initial={{ scale: 1, y: 0, rotate: 35, opacity: 0 }}
      animate={{ scale: 1, y: 0, rotate: 0, opacity: 1 }}
      whileHover={motionConfig.whileHover}
      whileTap={{ scale: 0.95 }}
      transition={{ type: "spring", stiffness: 800, damping: 10 }}
      className={cn("z-10 transform", motionConfig.staticRotation, className)}
    >
      {variant === "default" && (
        <Link
          to={linkHref}
          className={cn(pilotBadgeVariants({ variant }))}
          style={shimmerStyle}
        >
          <AirplaneTakeoff
            className={iconSize}
            color="hsl(var(--pilot-text))"
            style={{ zIndex: 10 }}
            weight="bold"
          />
          {text && <span className="relative z-10 font-mono">{t(text)}</span>}
        </Link>
      )}
      {variant !== "default" && (
        <motion.div
          className={cn(pilotBadgeVariants({ variant }))}
          style={shimmerStyle}
          whileHover={motionConfig.whileHover}
          transition={{ type: "spring", stiffness: 100, damping: 10 }}
        >
          <AirplaneTakeoff
            className={iconSize}
            color="hsl(var(--pilot-text))"
            style={{ zIndex: 10 }}
            weight="bold"
          />
          {text && <span className="relative z-10 font-mono">{t(text)}</span>}
        </motion.div>
      )}
    </motion.div>
  );
}

export default PilotBadge;
